import { debounce } from 'lodash';
import { useRef, useState, useEffect, useCallback } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { alpha } from '@mui/material/styles';
import {
  Box,
  Autocomplete,
  FormHelperText,
  IconButton,
  ListItemText,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  InputAdornment,
} from '@mui/material';

import axiosInstance from 'src/utils/axios';

import { useAppSelector } from 'src/redux/store';
import { TCreatePackagingDispatch } from 'src/redux/slices/packaging-dispatch';

import Iconify from 'src/components/iconify';
import { RHFTextField } from 'src/components/hook-form';

import { IEmployeeListItem } from 'src/types/employee';
import { IPackagingItemListItem } from 'src/types/packaging-item';
import { IPackagingProductListItem } from 'src/types/packaging-product';
import { IPackagingInsufficientInventoryItem } from 'src/types/packaging-dispatch';

interface Props {
  index: number;
  remove: (index: number) => void;
  isLast: boolean;
  // Seeds the ad-hoc search results with the line's already-selected item
  // (edit mode only) — otherwise the Autocomplete has no way to resolve a
  // display name for it before the user types a search query.
  initialItem?: IPackagingItemListItem;
  // Keyed by packagingItem id — set from the last failed submit's
  // insufficient-inventory response. Every line referencing a flagged
  // item is highlighted, not just one, since the shortfall is the
  // *combined* quantity across duplicate lines.
  insufficientItemsByPackagingItem?: Map<string, IPackagingInsufficientInventoryItem>;
  // Edit mode only: this line's saved quantity. The item's current stock
  // has already been reduced by it, so it's added back for the
  // "available" hint.
  originalQuantity?: number;
}

export default function PackagingDispatchNewItemRow({
  index,
  remove,
  isLast,
  initialItem,
  insufficientItemsByPackagingItem,
  originalQuantity,
}: Props) {
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [localItems, setLocalItems] = useState<IPackagingItemListItem[]>(
    initialItem ? [initialItem] : []
  );

  const { products } = useAppSelector((state) => state.packagingProduct);
  const { employees } = useAppSelector((state) => state.employees);

  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<TCreatePackagingDispatch>();

  const quantityInputRef = useRef<HTMLInputElement>(null);

  const defaultHelperText: string = errors.items?.[index] ? ' ' : '';

  // Existing lines (identified by a submitted `_id`) can't swap their
  // packagingItem on edit — a Return line may already reference this
  // dispatch item's id, so the backend silently keeps the original
  // packagingItem for any line carrying an `_id` regardless of what's
  // submitted. Locking it here avoids a misleading "successful" edit that
  // the server then ignores.
  const existingItemId = useWatch({ control, name: `items.${index}._id` as any });
  const isExistingLine = !!existingItemId;

  const watchedPackagingItemId = useWatch({
    control,
    name: `items.${index}.packagingItem` as any,
  });
  const insufficientItem = insufficientItemsByPackagingItem?.get(
    typeof watchedPackagingItemId === 'string'
      ? watchedPackagingItemId
      : (watchedPackagingItemId as IPackagingItemListItem | undefined)?._id ?? ''
  );

  const selectedItemId =
    typeof watchedPackagingItemId === 'string'
      ? watchedPackagingItemId
      : (watchedPackagingItemId as IPackagingItemListItem | undefined)?._id ?? '';

  // The selected item's current stock, for the "/ X unit" hint on the
  // quantity field (same as the kitchen/bar dispatch forms). Taken from the
  // search results when possible; otherwise fetched by id — edit-mode lines
  // only carry the item's code/name, not its stock.
  const [selectedItem, setSelectedItem] = useState<IPackagingItemListItem | null>(null);

  useEffect(() => {
    if (!selectedItemId) {
      setSelectedItem(null);
      return undefined;
    }
    if (selectedItem?._id === selectedItemId) {
      return undefined;
    }

    const fromSearch = localItems.find(
      (item) => item._id === selectedItemId && item.unit?.abbreviation !== undefined
    );
    if (fromSearch) {
      setSelectedItem(fromSearch);
      return undefined;
    }

    let cancelled = false;
    axiosInstance
      .get(`packaging/item/${selectedItemId}`)
      .then((response) => {
        if (!cancelled) setSelectedItem(response.data.data);
      })
      .catch((err) => console.warn(err));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId]);

  // A packaging item linked to a product always dispatches for that
  // product, so the Product field is filled in and locked (new lines only —
  // existing lines keep the product they were saved with).
  const linkedProductId =
    !isExistingLine && selectedItem && selectedItem._id === selectedItemId
      ? selectedItem.product?._id
      : undefined;

  const availableQuantity =
    selectedItem && selectedItem._id === selectedItemId
      ? selectedItem.quantity + (isExistingLine ? originalQuantity ?? 0 : 0)
      : null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPaginatedPackagingItems = useCallback(
    debounce(async (query) => {
      try {
        const response = await axiosInstance.get(
          `packaging/item?page=&limit=&generalSearch=${query}&itemName=&itemCode=&minQuantity=&maxQuantity=&unit=&alertEnabled=&status=active&sortBy=itemName&sort=asc`
        );

        setLocalItems(response.data.data.items);
      } catch (err) {
        console.warn(err);
      }
    }, 500),
    []
  );

  useEffect(() => {
    fetchPaginatedPackagingItems(itemSearchQuery);

    return fetchPaginatedPackagingItems.cancel;
  }, [itemSearchQuery, fetchPaginatedPackagingItems]);

  return (
    <TableRow
      sx={
        insufficientItem
          ? { bgcolor: (theme) => alpha(theme.palette.error.main, 0.08) }
          : undefined
      }
    >
      <TableCell>
        {index + 1 < 10 ? `0${index + 1}` : index + 1}

        <FormHelperText>{defaultHelperText}</FormHelperText>
      </TableCell>

      <TableCell sx={{ minWidth: 240 }}>
        {isExistingLine ? (
          <ListItemText
            primary={initialItem?.itemName}
            secondary={initialItem ? `${initialItem.itemCode} — cannot be changed` : undefined}
            primaryTypographyProps={{ typography: 'body2' }}
            secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
          />
        ) : (
          <Controller
            name={`items.${index}.packagingItem`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                fullWidth
                size="small"
                multiple={false}
                options={localItems}
                isOptionEqualToValue={(
                  option: IPackagingItemListItem | string,
                  value: IPackagingItemListItem | string
                ) => {
                  const optionId = typeof option === 'string' ? option : option?._id;
                  const valueId = typeof value === 'string' ? value : value?._id;
                  return optionId === valueId;
                }}
                getOptionLabel={(option: IPackagingItemListItem | string) => {
                  if (!option) return '';
                  if (typeof option === 'string') {
                    if (option.length === 0) return option;
                    const value = localItems.find(
                      (item: IPackagingItemListItem) => item._id === option
                    );
                    return value ? value.itemName : '';
                  }
                  return option.itemName;
                }}
                renderOption={(props, option: IPackagingItemListItem | string) => {
                  if (typeof option === 'string') {
                    return (
                      <li {...props} key={option}>
                        {option}
                      </li>
                    );
                  }
                  return (
                    <li {...props} key={option._id}>
                      {option.itemName} ({option.itemCode})
                    </li>
                  );
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') setItemSearchQuery(value);
                  if (reason === 'clear') setItemSearchQuery('');
                }}
                renderInput={(params) => (
                  <TextField
                    error={!!error}
                    required
                    helperText={error ? error.message : defaultHelperText}
                    {...params}
                  />
                )}
                onChange={(event, newValue) => {
                  const item = typeof newValue === 'string' ? null : newValue;
                  field.onChange(typeof newValue === 'string' ? newValue : item?._id ?? '');

                  if (item?.product?._id) {
                    setValue(`items.${index}.product`, item.product._id, { shouldValidate: true });
                  } else if (linkedProductId) {
                    // Switching away from a linked item: release the
                    // product it had auto-selected.
                    setValue(`items.${index}.product`, '');
                  }

                  // Quantity starts empty for each newly picked item, with
                  // the cursor already in it for quick entry.
                  setValue(`items.${index}.quantity`, 0);
                  clearErrors(`items.${index}.quantity`);
                  if (item) {
                    setTimeout(() => quantityInputRef.current?.focus(), 0);
                  }
                }}
              />
            )}
          />
        )}
      </TableCell>

      <TableCell sx={{ minWidth: 220 }}>
        <Controller
          name={`items.${index}.product`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              fullWidth
              size="small"
              multiple={false}
              disabled={!!linkedProductId}
              options={products.items}
              isOptionEqualToValue={(
                option: IPackagingProductListItem | string,
                value: IPackagingProductListItem | string
              ) => {
                const optionId = typeof option === 'string' ? option : option?._id;
                const valueId = typeof value === 'string' ? value : value?._id;
                return optionId === valueId;
              }}
              getOptionLabel={(option: IPackagingProductListItem | string) => {
                if (!option) return '';
                if (typeof option === 'string') {
                  if (option.length === 0) return option;
                  const value = products.items.find(
                    (item: IPackagingProductListItem) => item._id === option
                  );
                  return value ? value.productName : '';
                }
                return option.productName;
              }}
              renderOption={(props, option: IPackagingProductListItem | string) => {
                if (typeof option === 'string') {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }
                return (
                  <li {...props} key={option._id}>
                    {option.productName}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  error={!!error}
                  required
                  helperText={error ? error.message : defaultHelperText}
                  {...params}
                />
              )}
              onChange={(event, newValue) => {
                field.onChange(typeof newValue === 'string' ? newValue : newValue?._id ?? '');
              }}
            />
          )}
        />
      </TableCell>

      <TableCell sx={{ minWidth: 120 }}>
        <RHFTextField
          fullWidth
          required
          type="number"
          size="small"
          name={`items.${index}.quantity`}
          inputRef={quantityInputRef}
          helperText={
            insufficientItem
              ? `Only ${insufficientItem.availableQuantity} available`
              : defaultHelperText
          }
          FormHelperTextProps={insufficientItem ? { sx: { color: 'error.main' } } : undefined}
          showError={false}
          InputProps={{
            endAdornment: availableQuantity !== null && (
              <InputAdornment position="end">
                <Box sx={{ typography: 'subtitle2', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                  {`/ ${availableQuantity} ${selectedItem?.unit?.abbreviation ?? ''}`.trim()}
                </Box>
              </InputAdornment>
            ),
          }}
        />
      </TableCell>

      <TableCell sx={{ minWidth: 220 }}>
        <Controller
          name={`items.${index}.receivedBy`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              fullWidth
              size="small"
              multiple={false}
              options={employees.employees}
              isOptionEqualToValue={(
                option: IEmployeeListItem | string,
                value: IEmployeeListItem | string
              ) => {
                const optionId = typeof option === 'string' ? option : option?._id;
                const valueId = typeof value === 'string' ? value : value?._id;
                return optionId === valueId;
              }}
              getOptionLabel={(option: IEmployeeListItem | string) => {
                if (!option) return '';
                if (typeof option === 'string') {
                  if (option.length === 0) return option;
                  const value = employees.employees.find(
                    (employee: IEmployeeListItem) => employee._id === option
                  );
                  return value
                    ? `${value.firstName}${value.lastName ? ` ${value.lastName}` : ''} (${value.employeeNumber})`
                    : '';
                }
                return `${option.firstName}${option.lastName ? ` ${option.lastName}` : ''} (${option.employeeNumber})`;
              }}
              renderOption={(props, option: IEmployeeListItem | string) => {
                if (typeof option === 'string') {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }
                return (
                  <li {...props} key={option._id}>
                    {option.firstName}
                    {option.lastName ? ` ${option.lastName}` : ''} ({option.employeeNumber})
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  label="Received By"
                  error={!!error}
                  required
                  helperText={error ? error.message : defaultHelperText}
                  {...params}
                />
              )}
              onChange={(event, newValue) => {
                field.onChange(typeof newValue === 'string' ? newValue : newValue?._id ?? '');
              }}
            />
          )}
        />
      </TableCell>

      <TableCell align="right">
        <Tooltip title={isLast ? 'Last item cannot be deleted' : 'Delete'}>
          <span>
            <IconButton disabled={isLast} color="error" onClick={() => remove(index)}>
              <Iconify icon="solar:trash-bin-trash-bold" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
